import { Baseline } from "lucide-react";
import { KEYS } from "platejs";
import { useEditorRef, useEditorSelector } from "platejs/react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { RedoColorPicker } from "../../arbiter-components/color-picker/redo-color-picker";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useToolbarOverflow } from "./toolbar-overflow";

export const ColorToolbarButton = memo(function ColorToolbarButton() {
  const editor = useEditorRef();
  const [open, setOpen] = useState(false);

  const selectionDefined = useEditorSelector(
    (editor) => !!editor.selection,
    [],
  );

  const color = useEditorSelector(
    (editor) => editor.api.mark(KEYS.color) as string,
    [],
  );

  const [selectedColor, setSelectedColor] = useState<string>("#000000");

  useEffect(() => {
    if (selectionDefined && color) {
      setSelectedColor(color);
    }
  }, [color, selectionDefined]);

  const handleChange = useCallback(
    (value: string) => {
      setSelectedColor(value);
      if (editor.selection) {
        editor.tf.select(editor.selection);
        editor.tf.focus();
        editor.tf.addMarks({ [KEYS.color]: value });
      }
    },
    [editor],
  );

  const ColorIcon = useMemo(
    () =>
      function ColorIcon() {
        return (
          <div className="flex flex-col items-center">
            <Baseline className="size-3.5" />
            <div
              className="h-0.5 w-3.5 rounded-full"
              style={{ backgroundColor: selectedColor }}
            />
          </div>
        );
      },
    [selectedColor],
  );

  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <Baseline />
          Text color
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-auto p-3">
          <RedoColorPicker onChange={handleChange} value={selectedColor} />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <Popover modal={false} onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={ColorIcon}
            size="md"
            text=""
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-3"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <RedoColorPicker onChange={handleChange} value={selectedColor} />
      </PopoverContent>
    </Popover>
  );
});
